import React, { useRef, useEffect, forwardRef, MutableRefObject } from 'react';
import Hls from 'hls.js';
import AbstractPlayerInterface from './HTML5PlayerInterface';
import VideoComponent from './VideoComponent';

interface IHLSPlayerProps {
  src: string;
  startFrom?: number;
  width?: number;
  height?: number;
  playerType: string;
  forwardedRef: MutableRefObject<AbstractPlayerInterface>;
  onVideoEvent: (type: string) => void;
}

const HLSPlayer: React.FC<IHLSPlayerProps> = (props) => {
  const { src, startFrom = 0, width, height, forwardedRef, onVideoEvent } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const HLSVideoRef = useRef<Hls>(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!src) return;

    const onPlayerOpen = () => {
      const HLSVideo = new Hls({ enableWorker: false });

      HLSVideo.attachMedia(videoRef.current);
      HLSVideo.loadSource(src);

      HLSVideo.on(Hls.Events.MANIFEST_PARSED, () => {
        HLSVideo.startLoad(startFrom);
      });

      HLSVideo.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            // try to recover network error
            HLSVideo.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            HLSVideo.recoverMediaError();
            break;
          default:
            HLSVideo.destroy();
            break;
        }
      });
      HLSVideoRef.current = HLSVideo;
    };

    timeoutRef.current = setTimeout(() => onPlayerOpen(), 500);

    return () => {
      if (HLSVideoRef.current) {
        HLSVideoRef.current.destroy();
        HLSVideoRef.current = null;
      }
      clearTimeout(timeoutRef.current);
    };
  }, [src, startFrom]);

  useEffect(() => {
    forwardedRef.current = new AbstractPlayerInterface(videoRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VideoComponent ref={videoRef} width={width} height={height} onEvent={onVideoEvent} key={src} />
  );
};

export default forwardRef(
  (
    props: Omit<IHLSPlayerProps, 'forwardedRef'>,
    ref: MutableRefObject<AbstractPlayerInterface>,
  ) => <HLSPlayer forwardedRef={ref} {...props} />,
);
